"use client";
import { ContentfulImage } from "@2u-landing-pages/components/ContentfulImage";
import { ContentfulImageConfig, TypeHeroWithoutUnresolvableLinksResponse } from "@2u-landing-pages/types";
import { getGradientOverlayStyle } from "@2u-landing-pages/utils/gradient-overlay-config";
import cn from "classnames";
import React from "react";
// @ts-ignore
import styles from "./TemplateWrapper.module.scss";

interface TemplateWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  as?: "section" | "div" | "header" | "footer";
  backgroundColor?: string;
  padded?: boolean;
  drawer?: boolean;
  paddingTop?: string;
  paddingBottom?: string;
  backgroundImage?: TypeHeroWithoutUnresolvableLinksResponse["fields"]["background_image"];
  image_config?: ContentfulImageConfig;
  inspector?: (props: { entryId: string; fieldId: string }) => Record<string, unknown>;
  backgroundImageEntryId?: string;
  backgroundImageFieldId?: string;
}

interface CustomCSSProperties extends React.CSSProperties {
  "--surface-background-color"?: string;
  "--focal-point-desktop"?: string;
  "--focal-point-mobile"?: string;
}

type PaddingMap = { [index: string]: string };

// Map the contentful string values to values used in the css classes
const paddingMap: PaddingMap = {
  "Large (64/32)": "large",
  "Medium (48/24)": "medium",
  "Small (32/16)": "small",
  "X-Small (24/12)": "xsmall",
};

export const TemplateWrapper: React.FC<TemplateWrapperProps> = ({
  as = "section",
  children,
  className,
  padded,
  paddingTop = "Large (64/32)",
  paddingBottom = "Large (64/32)",
  backgroundColor,
  backgroundImage,
  image_config,
  style: styleProp,
  drawer,
  inspector,
  backgroundImageEntryId,
  backgroundImageFieldId,
  ...rest
}) => {
  let style: CustomCSSProperties = {
    ...styleProp,
  };
  const imageConfig = image_config as unknown as ContentfulImageConfig;

  const desktopCfg = imageConfig?.default;
  const mobileCfg = imageConfig?.mobile;
  const hasBackgroundImage = Boolean(backgroundImage?.fields?.file?.url);

  const desktopOverlayStyle = desktopCfg ? getGradientOverlayStyle(desktopCfg, "desktop") : {};
  const mobileOverlayStyle = mobileCfg ? getGradientOverlayStyle(mobileCfg, "mobile") : {};

  if (backgroundColor) style["--surface-background-color"] = `var(${backgroundColor})`;

  if (hasBackgroundImage) {
    // Merge gradient overlay CSS vars so the ::before pseudo-element on the image container can read them
    Object.assign(style, desktopOverlayStyle, mobileOverlayStyle);
    if (imageConfig?.default) {
      style["--focal-point-desktop"] = desktopCfg?.focalPoint ?? "center";
      style["--focal-point-mobile"] = mobileCfg?.focalPoint ?? desktopCfg?.focalPoint ?? "center";
    }
  }

  const Tag = as;

  return (
    <Tag
      className={cn(styles.templateWrapper, className, {
        [styles.padded]: padded,
        [styles[`padding-top-${paddingMap[paddingTop]}`]]: padded,
        [styles[`padding-bottom-${paddingMap[paddingBottom]}`]]: padded,
        [styles.drawer]: drawer,
        [styles.hasBackgroundImage]: hasBackgroundImage,
      })}
      style={style}
      {...rest}
    >
      {hasBackgroundImage && (
        <div
          className={cn(styles.backgroundImageContainer, {
            [styles.solidOverlay]: [desktopCfg, mobileCfg].some((cfg) => cfg?.gradientType === "solid"),
            [styles.gradientOverlay]: [desktopCfg, mobileCfg].some((cfg) => cfg?.gradientType === "gradient"),
            [styles.hasBackgroundColor]: Boolean(backgroundColor),
          })}
        >
          <ContentfulImage
            fill
            image={backgroundImage}
            config={imageConfig}
            component="section background"
            {...(inspector && backgroundImageEntryId
              ? inspector({ entryId: backgroundImageEntryId, fieldId: backgroundImageFieldId ?? "background_image" })
              : {})}
          />
        </div>
      )}
      {children}
    </Tag>
  );
};
